import { ButtonHTMLAttributes, ReactNode } from "react";
import LoadingLottie from "@/shared/ui/loading/LoadingLottie";
import { useTapInteraction } from "@/shared/ui/pressable";
import styles from "./PrimaryButton.module.scss";

type PrimaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  width?: string;
  height?: string;
  loading?: boolean;
  /** true 면 각진 모서리(border-radius 0) — 키보드 부착 CTA 등 edge-to-edge 용도. */
  square?: boolean;
};

const PrimaryButton = ({
  children,
  width = "100%",
  height = "3.25rem",
  loading = false,
  square = false,
  disabled,
  className = "",
  style,
  ...props
}: PrimaryButtonProps) => {
  const isInactive = Boolean(disabled) || loading;
  const { isPressed, ref, pressHandlers } = useTapInteraction<HTMLButtonElement>({
    disabled: isInactive,
  });

  return (
    <button
      {...props}
      {...pressHandlers}
      ref={ref}
      // 로딩 중에는 disabled 회색을 칠하지 않는다 — primary 배경 위 로더를 유지해
      // "완성도 disabled(회색) → 로딩(회색+로더)" 로 이어질 때의 배경 플래시를 없앤다.
      // 상호작용 차단은 아래 disabled={isInactive} 가 그대로 담당(게이트 보존).
      className={`${styles.primaryButton} ${square ? styles.square : ""} ${disabled && !loading ? styles.disabled : ""} ${isPressed ? styles.pressed : ""} ${className}`.trim()}
      style={{ width, height, ...style }}
      disabled={isInactive}
      aria-busy={loading || undefined}
    >
      {loading ? (
        // 시각은 흰 4점 로띠만, 접근가능 이름은 children 을 시각 숨김으로 보존(aria-busy 는 상태 힌트일 뿐).
        <>
          <LoadingLottie
            variant="white"
            size="44px"
          />
          <span className="visually-hidden">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default PrimaryButton;
